import { useState, useEffect } from 'react';
import { Search, Music, Clock, X, Loader2 } from 'lucide-react';
import { SongSearchResult, formatDuration, isExplicit } from '../../services/itunesApi';
import { searchSongs } from '../../services/songSearch';
import type { SearchCatalog } from '../../services/playModes';
import { createRequest } from '../../services/requests';
import { claimPrize } from '../../services/prizes';
import { getAttendeeNickname } from '../../services/attendeeIdentity';
import { prizePickSkipsApproval } from '../../utils/autoApproval';
import toast from 'react-hot-toast';

interface PrizeSongPickerProps {
  eventId: string;
  prizeId: string;
  attendeeId: string;
  expiresAt?: string;
  blockExplicit?: boolean;
  coApprovalEnabled: boolean;
  autoApprovalHold: boolean;
  searchCatalog: SearchCatalog;
  onComplete: () => void;
  onCancel: () => void;
}

function useCountdown(expiresAt?: string): string {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('Expired');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return remaining;
}

export default function PrizeSongPicker({
  eventId,
  prizeId,
  attendeeId,
  expiresAt,
  blockExplicit = false,
  coApprovalEnabled,
  autoApprovalHold,
  searchCatalog,
  onComplete,
  onCancel,
}: PrizeSongPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SongSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const countdown = useCountdown(expiresAt);
  const skipsApproval = prizePickSkipsApproval({ settings: { coApprovalEnabled }, autoApprovalHold });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const songs = await searchSongs(searchQuery.trim(), 10, 'song', searchCatalog);
      setResults(blockExplicit ? songs.filter(s => !isExplicit(s)) : songs);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = async (song: SongSearchResult) => {
    setSubmitting(true);
    try {
      const nickname = getAttendeeNickname();
      const request = await createRequest(eventId, {
        song: {
          title: song.trackName,
          artist: song.artistName,
          albumArtUrl: song.artworkUrl100,
          durationMs: song.trackTimeMillis,
          explicitFlag: isExplicit(song),
          itunesTrackId: String(song.trackId),
          previewUrl: song.previewUrl,
          isrc: song.isrc ?? null,
        },
        requestedBy: {
          oderId: attendeeId,
          nickname: nickname || undefined,
        },
        ...(skipsApproval ? { initialStatus: 'queued' as const } : {}),
        prizeSkipTheLine: true,
        prizeId,
      });

      await claimPrize(prizeId, {
        songTitle: song.trackName,
        songArtist: song.artistName,
        albumArtUrl: song.artworkUrl100,
        requestId: request.id,
      });

      toast.success(coApprovalEnabled
        ? "Song picked! Once it's approved, it plays next."
        : skipsApproval
          ? 'Song picked! It goes straight to the top of the queue.'
          : 'Song picked! Once the DJ approves it, it goes to the top of the queue.');
      onComplete();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit song');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <Music className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-semibold text-white">Pick Your Song</h2>
        </div>
        <div className="flex items-center gap-3">
          {countdown && countdown !== 'Expired' && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-800 text-sm">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-amber-400 font-medium">{countdown}</span>
            </div>
          )}
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="p-4 bg-gray-900">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search for a song..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              autoFocus
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            className="px-4 py-2.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-medium disabled:opacity-50 transition-colors"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-950 p-4">
        {results.length === 0 && !searching && (
          <p className="text-center text-gray-500 mt-8">Search for a song to pick as your prize</p>
        )}
        {searching && (
          <div className="flex items-center justify-center mt-8">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          </div>
        )}
        <div className="space-y-2">
          {results.map((song) => (
            <button
              key={song.trackId}
              onClick={() => handleSelect(song)}
              disabled={submitting}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-900 hover:bg-gray-800 transition-colors text-left disabled:opacity-50"
            >
              {song.artworkUrl100 ? (
                <img src={song.artworkUrl100} alt="" className="w-12 h-12 rounded-md flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-md bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-gray-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{song.trackName}</p>
                <p className="text-gray-400 text-sm truncate">{song.artistName}</p>
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {formatDuration(song.trackTimeMillis)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {submitting && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
          <div className="flex items-center gap-3 px-6 py-4 bg-gray-900 rounded-xl">
            <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
            <span className="text-white font-medium">Submitting your pick...</span>
          </div>
        </div>
      )}
    </div>
  );
}
